import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import GuruLayout from "../../components/layout/GuruLayout";
import {
  QrCode,
  ClipboardEdit,
  ArrowRight,
  CheckCheck,
  AlertTriangle,
} from "lucide-react";
import { usePopup } from "../../components/modal/Popup";

const API = "https://backend.smpn1kamal.sch.id/api";

export default function Presensi() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // JADWAL AKTIF
  const [jadwalAktif, setJadwalAktif] = useState(null);

  const { success: popSuccess, error: popError, PopupPortal } = usePopup();

  // AMBIL JADWAL AKTIF
  useEffect(() => {
    const fetchJadwalAktif = async () => {
      try {
        const token = sessionStorage.getItem("token");

        const res = await axios.get(`${API}/jadwal/aktif`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (res.data?.active) {
          setJadwalAktif(res.data.jadwal);
        }
      } catch (err) {
        console.error("Gagal mengambil jadwal aktif:", err);
      }
    };

    fetchJadwalAktif();
  }, []);

  const hadirSemua = async () => {
    setShowConfirmModal(false);
    const token = sessionStorage.getItem("token");
    setLoading(true);

    try {
      const res = await axios.post(
        `${API}/hadir-semua`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      popSuccess("Berhasil", res.data.message);
    } catch (err) {
      popError(
        "Gagal",
        err.response?.data?.message || "Gagal menandai hadir semua"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <GuruLayout>
      {PopupPortal}

      <div className="max-w-7xl mx-auto space-y-6 pb-10">

        {/* ===== HEADER SECTION ===== */}
        <div className="w-full flex flex-row items-center justify-between gap-4 border-b border-slate-100 pb-6 mb-4">
          <div className="text-left min-w-0 flex-1">
            <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-800 tracking-tight whitespace-nowrap">
              Presensi <span className="text-blue-600">Hari Ini</span>
            </h1>

            <p className="text-slate-500 mt-1 text-xs sm:text-sm truncate">
              Pilih metode presensi untuk memulai kelas.
            </p>
          </div>

          {/* Tombol Tetap di Kanan */}
          <div className="flex-shrink-0">
            <button
              onClick={() => setShowConfirmModal(true)}
              disabled={loading}
              className="flex items-center gap-1.5 sm:gap-2 bg-indigo-500 text-white px-4 sm:px-5 py-2 rounded-full text-xs sm:text-sm hover:bg-indigo-600 transition font-medium shadow-sm active:scale-95 disabled:opacity-50 whitespace-nowrap"
            >
              <CheckCheck size={16} />
              {loading ? "Memproses..." : "Hadir Semua"}
            </button>
          </div>
        </div>

        {/* ===== CARD GRID ===== */}
        <div className="grid md:grid-cols-2 gap-6 w-full">

          {/* Card Scan QR */}
          <button
            onClick={() => navigate("/guru/presensi/scanner")}
            className="group relative bg-white p-8 rounded-3xl border border-slate-100 shadow-sm hover:shadow-xl hover:border-blue-100 transition-all duration-300 text-left overflow-hidden"
          >
            <div className="absolute top-0 right-0 p-4 text-slate-100 opacity-40 group-hover:opacity-60 group-hover:text-blue-50 transition-all duration-300">
              <QrCode size={140} />
            </div>

            <div className="w-14 h-14 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-blue-600 group-hover:text-white transition-colors duration-300 shadow-sm shadow-blue-100">
              <QrCode size={26} />
            </div>

            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              Scan QR Code
            </h2>

            <p className="text-slate-500 text-sm leading-relaxed mb-6 max-w-sm font-medium">
              Arahkan kamera perangkat ke kode QR kartu siswa untuk melakukan
              pencatatan kehadiran otomatis secara instan.
            </p>

            <div className="flex items-center text-sm font-semibold text-blue-600">
              Mulai Scanning{" "}
              <ArrowRight
                size={16}
                className="ml-2 group-hover:translate-x-2 transition-transform duration-300"
              />
            </div>
          </button>

          {/* Card Input Manual */}
          <button
            onClick={() => navigate("/guru/presensi/manual")}
            className="group relative bg-white p-8 rounded-3xl border border-gray-100 shadow-sm hover:shadow-xl hover:border-indigo-100 transition-all duration-300 text-left overflow-hidden"
          >
            <div className="absolute top-0 right-0 p-4 text-slate-100 opacity-40 group-hover:opacity-60 group-hover:text-indigo-50 transition-all duration-300">
              <ClipboardEdit size={140} />
            </div>

            <div className="w-14 h-14 bg-indigo-50 text-indigo-600 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-indigo-600 group-hover:text-white transition-colors duration-300 shadow-sm shadow-indigo-100">
              <ClipboardEdit size={26} />
            </div>

            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              Input Manual
            </h2>

            <p className="text-slate-500 text-sm leading-relaxed mb-6 max-w-sm font-medium">
              Pilih dan kelola daftar nama siswa secara manual melalui lembar
              kehadiran jika terjadi kendala teknis pada perangkat.
            </p>

            <div className="flex items-center text-sm font-semibold text-indigo-600">
              Buka Daftar Hadir{" "}
              <ArrowRight
                size={16}
                className="ml-2 group-hover:translate-x-2 transition-transform duration-300"
              />
            </div>
          </button>

        </div>

      </div>

      {/* ===== POPUP KONFIRMASI ===== */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-sm flex items-center justify-center z-[999] p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-[32px] p-8 max-w-md w-full shadow-2xl text-center border border-slate-100 transform transition-all animate-in zoom-in-95 duration-200">

            <div className="w-16 h-16 bg-amber-50 text-amber-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm shadow-amber-100">
              <AlertTriangle size={32} />
            </div>

            <h3 className="text-xl font-bold text-slate-800 tracking-tight mb-2">
              Konfirmasi Kehadiran
            </h3>

            <p className="text-sm text-slate-500 leading-relaxed mb-6">
              Apakah Anda yakin ingin menandai seluruh siswa sebagai hadir pada
              mata pelajaran{" "}
              <span className="font-semibold text-slate-700">
                {jadwalAktif?.nama_mapel || "-"}
              </span>{" "}
              di kelas{" "}
              <span className="font-semibold text-slate-700">
                {jadwalAktif?.nama_kelas || "-"}
              </span>{" "}
              pukul{" "}
              <span className="font-semibold text-slate-700">
                {jadwalAktif?.jam_mulai?.slice(0, 5) || "--:--"} -{" "}
                {jadwalAktif?.jam_selesai?.slice(0, 5) || "--:--"}
              </span>
              ? Tindakan ini akan mengubah status absen sementara.
            </p>

            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setShowConfirmModal(false)}
                className="w-full py-3 bg-slate-100 text-slate-600 hover:bg-slate-200 rounded-full font-bold text-xs uppercase tracking-widest transition-all active:scale-95"
              >
                Batal
              </button>

              <button
                type="button"
                onClick={hadirSemua}
                className="w-full py-3 bg-indigo-500 text-white hover:bg-indigo-600 rounded-full font-bold text-xs uppercase tracking-widest transition-all active:scale-95 shadow-md shadow-indigo-100"
              >
                Ya, Hadirkan
              </button>
            </div>

          </div>
        </div>
      )}
    </GuruLayout>
  );
}